import React, {useState, useRef, useLayoutEffect} from 'react';
import {
  chakra,
  Box,
  Stack, HStack,
  Divider,
  Heading,
  Text,
  Button,
  Image,
  AccordionItem, AccordionButton, AccordionPanel, AccordionItemProps,
  OrderedList, ListItem, ListProps
} from '@chakra-ui/react';
import useResizeObserver from '@react-hook/resize-observer';
import {getHoverColor} from 'chart.js/helpers';
import {DIVIDUALS_CHART_CLASS_NAME, estateId} from '../aggregateData';
import RatiosChart from './RatiosChart';
import DividualsChart from './DividualsChart';
import RatioInputs from './RatioInputs';
import ComfortInputs from './ComfortInputs';
import { IMAGE_PREFIX } from '../env';
import { CHART_COLORS } from '../theme';
import DividualsChartHeading from './DividualsChartHeading';

type Props = AccordionItemProps & {
  estate: Estate,
  setEstate(estate: Estate): void,
  estates: Estate[]
};

export default function Dividuals({estate, setEstate, estates, ...props}: Props) {
  const [isDividualsChartShown, setIsDividualsChartShown] = useState(false);
  const dividualsChartRef = useRef<HTMLDivElement>(null);
  const [dividualsChartHeight, setDividualsChartHeight] = useState(0);
  const [hovered, setHovered] = useState<number>();
  useLayoutEffect(() => setDividualsChartHeight(dividualsChartRef.current?.getBoundingClientRect().height ?? 0), [dividualsChartRef, setDividualsChartHeight]);
  useResizeObserver(dividualsChartRef, entry => setDividualsChartHeight(entry.contentRect.height));
  const setComfort = (index: number, comfort: Comfort) => {
    setEstate({
      ...estate,
      dividuals: [
        ...estate.dividuals.slice(0, index),
        {...estate.dividuals[index], comfort},
        ...estate.dividuals.slice(index + 1)
      ]
    });
  };

  return (
    <AccordionItem {...props} id={estateId(estate)} border="solid 1px" borderRadius="3xl" overflow="hidden">
      {({isExpanded}) => (
        <>
          <Heading as="h3">
            <AccordionButton
              display="flex"
              justifyContent="space-between"
              w="full"
              p={isExpanded ?
                [`21px 24px 15px 16px`, `21px 24px 15px 16px`, `${40.9/16}rem ${43.1/16}rem ${31/16}rem ${58.5/16}rem`] :
                ['24px 22px 24px 16px', '24px 22px 24px 16px', `${25.9/16}rem ${40.1/16}rem ${23.9/16}rem ${68.5/16}rem`]}
              transition="padding 200ms"
              wordBreak="break-word"
            >
              <Box fontSize={['lg', 'lg', '1.75rem']}>
                <chakra.span fontSize={['3xl', '3xl', "4xl"]} mr={[0, 0, `${30.5/16}rem`]}>“{estate.name}”</chakra.span>のとき
              </Box>
              <chakra.span role="presentation">{isExpanded ? 'ー' : '▼'}</chakra.span>
            </AccordionButton>
          </Heading>
          <Divider mx={[4, 4, 8]} w="auto" borderColor="gray.200" />
          <AccordionPanel p="0 0 26px">
            <Stack direction={['column', 'column', 'row']} spacing={[6, 6, `${45/16}rem`]} justify="space-between" mt={[6, 6, `${38/16}rem`]} mx="auto" w={['91.30434782608695%', '91.30434782608695%', "87.5%"]}>
              <Box w={[`${20000/315}%`, `${20000/315}%`, `${29000/897}%`]} mx="auto" flexShrink="0">
                <RatiosChart estate={estate} />
              </Box>
              <Box h="auto" w="auto">
                <RatioInputs
                  estate={estate}
                  setEstate={setEstate}
                  estates={estates}
                  flexBasis={`${562/16}rem`}
                  pt={[4, 4, `${58/16}rem`]}
                  px={[2.5, 2.5, "8"]}
                  pb={[9, 9, `${42/16}rem`]}
                  borderRadius="3xl"
                  bgColor="gray.100"
                />
                <Box mt={[2.5, 2.5, "4"]} lineHeight="1.5" color="gray.500">
                  <Text>※「分人」は10つまで入力することが出来ます。</Text>
                  <Text>※STEP1で “分人を振り返ってみたい時期”を複数入力された方は、1つめの分人 グラフを完成させた後に、2つめ以降の分人構成比率入力欄が表示されます。</Text>
                </Box>
              </Box>
            </Stack>
            <ComfortInputs estate={estate} setComfort={setComfort} />
            <Box display={isDividualsChartShown ? 'none' : 'block'} className="btn-large" textAlign="center" sx={{'button::after': {display: 'none'}}}>
              <Button
                type="button"
                onClick={() => setIsDividualsChartShown(true)}
                h="inherit"
                transform="translateY(-50%)"
                bgColor="white"
              >円グラフに反映する</Button>
            </Box>
            <Box 
              visibility={isDividualsChartShown ? 'visible' : 'hidden'}
              h={isDividualsChartShown ? `${dividualsChartHeight}px` : 0}
              mt={isDividualsChartShown ? [10, 10, `${118/16}rem`] : 0}
              overflowY="hidden"
              transition="visibility 200ms, height 200ms"
            >
              <Box ref={dividualsChartRef} textAlign="center">
                <DividualsChartHeading name={estate.name} />
                <Box mt={[`${26/16}rem`, `${26/16}rem`, `${55/16}rem`]} mx="auto" w="66.89453125%" className={DIVIDUALS_CHART_CLASS_NAME}>
                  <DividualsChart estate={estate} onHover={setHovered} />
                  <Legends estate={estate} hovered={hovered} mt="4" />
                </Box>
              </Box>
            </Box>
          </AccordionPanel>
        </>
      )}
    </AccordionItem>
  );
}

type LegendsProps = ListProps & {
  estate: Estate,
  hovered?: number
};

function Legends({estate: {dividuals}, hovered, ...props}: LegendsProps) {
  const sum = dividuals.reduce((s, {ratio}) => s + ratio, 0);
  const percentages: number[] = [];
  for (let i = 0, l = dividuals.length; i < l; i++) {
    if (i === l - 1) {
      const currentSum = percentages.reduce((s, p) => s + p, 0);
      percentages.push(100 - currentSum);
    } else {
      percentages.push(Math.round(dividuals[i].ratio * 100 / sum));
    }
  }

  return (
    <OrderedList
      listStyleType="none"
      display="grid"
      gridTemplateColumns={['1fr', '1fr', '1fr 1fr']}
      columnGap="4"
      rowGap="2"
      m="0"
      fontSize="sm"
      lineHeight="1"
      {...props}
    >
      {dividuals.map(({id, name, comfort}, index) => {
        const num = index < 9 ? `0${index + 1}` : `${index + 1}`;
        const imgOn = `${IMAGE_PREFIX}icon-heart-${num}_on.png`;
        const imgOff = `${IMAGE_PREFIX}icon-heart-${num}_off.png`;
        const isHovered = index === hovered;
        const baseBgColor = CHART_COLORS[index].bg;
        const bgColor = isHovered ? getHoverColor(baseBgColor) : baseBgColor;

        return (
          <ListItem key={id} p={isHovered ? 'calc(0.5rem - 2px)' : '0.5rem'} borderWidth={isHovered ? '2px' : '0'} borderColor="gray.100" borderStyle="solid" color={CHART_COLORS[index].text} bgColor={bgColor}>
            <HStack alignItems={['flex-start', 'flex-start', 'center']}>
              <Box
                w="16"
                flexShrink="0"
                py="1"
                borderWidth="2px"
                borderColor={CHART_COLORS[index].text}
                bgColor={bgColor}
              >{index + 1}</Box>
              <Stack direction={['column', 'column', 'row']} spacing="2" justify="center" pt={[1, 1, 0]}>
                <HStack aria-label={comfort.toString()} spacing="1" flexShrink="0">
                  {[...Array(comfort)].map((_, i) => <Image key={i} src={imgOn} role="presentation" h="1em" />)}
                  {[...Array(5 - comfort)].map((_, i) => <Image key={comfort + i} src={imgOff} role="presentation" h="1em" />)}
                </HStack>
                <chakra.span textAlign="start">{name}({percentages[index]}%)</chakra.span>
              </Stack>
            </HStack>
          </ListItem>
        )
      })}
    </OrderedList>
  );
}
